import { useState, useMemo } from 'react';
import {
  Typography,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Popconfirm,
  message,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  LockOutlined,
  StopOutlined,
  CheckCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useToggleActive,
  useResetPassword,
  useRoles,
} from '@hooks/useAdmin';
import type {
  UserListItem,
  UserFilters,
  CreateUserRequest,
  UpdateUserRequest,
} from '@/types/admin';

const mdRoleColors: Record<string, string> = {
  Admin: 'red',
  Director: 'purple',
  StorekeeperManager: 'blue',
  SalesManager: 'green',
  PurchaseManager: 'orange',
  ServiceEngineer: 'cyan',
  Accountant: 'gold',
};

export default function UsersPage() {
  const [mdFilters, setMdFilters] = useState<UserFilters>({
    page: 1,
    pageSize: 20,
  });
  const [mdSearchText, setMdSearchText] = useState('');
  const [mdModalOpen, setMdModalOpen] = useState(false);
  const [mdEditingUser, setMdEditingUser] = useState<UserListItem | null>(null);
  const [mdForm] = Form.useForm();

  const { data: mdUsersData, isLoading: mdIsLoading } = useUsers(mdFilters);
  const { data: mdRolesList } = useRoles();
  const mdCreateUser = useCreateUser();
  const mdUpdateUser = useUpdateUser();
  const mdToggleActive = useToggleActive();
  const mdResetPassword = useResetPassword();

  const mdIsCreating = !mdEditingUser;

  const mdHandleSearch = () => {
    setMdFilters((mdPrev) => ({ ...mdPrev, search: mdSearchText || undefined, page: 1 }));
  };

  const mdHandleRoleFilter = (mdRole: string | undefined) => {
    setMdFilters((mdPrev) => ({ ...mdPrev, role: mdRole, page: 1 }));
  };

  const mdHandleActiveFilter = (mdValue: string | undefined) => {
    let mdIsActive: boolean | undefined;
    if (mdValue === 'true') mdIsActive = true;
    else if (mdValue === 'false') mdIsActive = false;
    setMdFilters((mdPrev) => ({ ...mdPrev, isActive: mdIsActive, page: 1 }));
  };

  const mdHandleOpenCreate = () => {
    setMdEditingUser(null);
    mdForm.resetFields();
    setMdModalOpen(true);
  };

  const mdHandleOpenEdit = (mdRecord: UserListItem) => {
    setMdEditingUser(mdRecord);
    mdForm.setFieldsValue({
      firstName: mdRecord.firstName,
      lastName: mdRecord.lastName,
      middleName: mdRecord.middleName ?? '',
      email: mdRecord.email,
      roles: mdRecord.roles,
    });
    setMdModalOpen(true);
  };

  const mdHandleModalOk = async () => {
    try {
      const mdValues = await mdForm.validateFields();
      if (mdIsCreating) {
        const mdRequest: CreateUserRequest = {
          userName: mdValues.userName,
          email: mdValues.email,
          password: mdValues.password,
          firstName: mdValues.firstName,
          lastName: mdValues.lastName,
          middleName: mdValues.middleName || undefined,
          roles: mdValues.roles ?? [],
        };
        await mdCreateUser.mutateAsync(mdRequest);
        message.success('Користувача створено');
      } else {
        const mdRequest: UpdateUserRequest = {
          id: mdEditingUser!.id,
          firstName: mdValues.firstName,
          lastName: mdValues.lastName,
          middleName: mdValues.middleName || undefined,
          email: mdValues.email,
          phoneNumber: mdValues.phoneNumber || undefined,
          roles: mdValues.roles ?? [],
        };
        await mdUpdateUser.mutateAsync(mdRequest);
        message.success('Користувача оновлено');
      }
      setMdModalOpen(false);
      mdForm.resetFields();
    } catch (mdErr) {
      // validation errors are handled by Form
      if (mdErr && typeof mdErr === 'object' && 'errorFields' in mdErr) return;
      message.error('Помилка збереження');
    }
  };

  const mdHandleToggleActive = async (mdRecord: UserListItem) => {
    try {
      await mdToggleActive.mutateAsync(mdRecord.id);
      message.success(
        mdRecord.isActive ? 'Користувача деактивовано' : 'Користувача активовано'
      );
    } catch {
      message.error('Помилка зміни статусу');
    }
  };

  const mdHandleResetPassword = async (mdRecord: UserListItem) => {
    try {
      const mdResult = await mdResetPassword.mutateAsync(mdRecord.id);
      Modal.info({
        title: 'Новий пароль',
        content: (
          <div>
            <p>Новий пароль для <strong>{mdRecord.userName}</strong>:</p>
            <Typography.Text code copyable>
              {mdResult.newPassword}
            </Typography.Text>
          </div>
        ),
      });
    } catch {
      message.error('Помилка скидання пароля');
    }
  };

  const mdColumns: ColumnsType<UserListItem> = useMemo(
    () => [
      {
        title: 'Логін',
        dataIndex: 'userName',
        key: 'userName',
        width: 140,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 200,
      },
      {
        title: "Ім'я",
        key: 'name',
        width: 200,
        render: (_: unknown, mdRecord: UserListItem) =>
          `${mdRecord.lastName} ${mdRecord.firstName}${mdRecord.middleName ? ' ' + mdRecord.middleName : ''}`,
      },
      {
        title: 'Ролі',
        dataIndex: 'roles',
        key: 'roles',
        width: 200,
        render: (mdRoles: string[]) =>
          mdRoles.map((mdRole) => (
            <Tag key={mdRole} color={mdRoleColors[mdRole] ?? 'default'}>
              {mdRole}
            </Tag>
          )),
      },
      {
        title: 'Активний',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 100,
        render: (mdIsActive: boolean) =>
          mdIsActive ? (
            <Tag color="green">Активний</Tag>
          ) : (
            <Tag color="red">Неактивний</Tag>
          ),
      },
      {
        title: 'Останній вхід',
        dataIndex: 'lastLoginAt',
        key: 'lastLoginAt',
        width: 160,
        render: (mdDate: string | undefined) =>
          mdDate ? dayjs(mdDate).format('DD.MM.YYYY HH:mm') : '—',
      },
      {
        title: 'Дії',
        key: 'actions',
        width: 200,
        render: (_: unknown, mdRecord: UserListItem) => (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => mdHandleOpenEdit(mdRecord)}
            >
              Редагувати
            </Button>
            <Popconfirm
              title={
                mdRecord.isActive
                  ? 'Деактивувати користувача?'
                  : 'Активувати користувача?'
              }
              onConfirm={() => mdHandleToggleActive(mdRecord)}
              okText="Так"
              cancelText="Ні"
            >
              <Button
                size="small"
                icon={mdRecord.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                danger={mdRecord.isActive}
              />
            </Popconfirm>
            <Popconfirm
              title="Скинути пароль?"
              description="Буде згенеровано новий пароль"
              onConfirm={() => mdHandleResetPassword(mdRecord)}
              okText="Так"
              cancelText="Ні"
            >
              <Button size="small" icon={<LockOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    []
  );

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Управління користувачами
      </Typography.Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Space>
            <Input
              placeholder="Пошук..."
              prefix={<SearchOutlined />}
              value={mdSearchText}
              onChange={(mdE) => setMdSearchText(mdE.target.value)}
              onPressEnter={mdHandleSearch}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Роль"
              allowClear
              style={{ width: 180 }}
              onChange={mdHandleRoleFilter}
              options={
                mdRolesList?.map((mdR) => ({ label: mdR, value: mdR })) ?? []
              }
            />
            <Select
              placeholder="Статус"
              allowClear
              style={{ width: 140 }}
              onChange={mdHandleActiveFilter}
              options={[
                { label: 'Активні', value: 'true' },
                { label: 'Неактивні', value: 'false' },
              ]}
            />
            <Button onClick={mdHandleSearch}>Шукати</Button>
          </Space>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={mdHandleOpenCreate}>
            Створити користувача
          </Button>
        </Col>
      </Row>

      <Table<UserListItem>
        rowKey="id"
        columns={mdColumns}
        dataSource={mdUsersData?.items ?? []}
        loading={mdIsLoading}
        pagination={{
          current: mdFilters.page,
          pageSize: mdFilters.pageSize,
          total: mdUsersData?.totalCount ?? 0,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdPage, mdSize) =>
            setMdFilters((mdPrev) => ({ ...mdPrev, page: mdPage, pageSize: mdSize })),
        }}
        scroll={{ x: 1200 }}
        size="middle"
      />

      <Modal
        title={mdIsCreating ? 'Створити користувача' : 'Редагувати користувача'}
        open={mdModalOpen}
        onOk={mdHandleModalOk}
        onCancel={() => {
          setMdModalOpen(false);
          mdForm.resetFields();
        }}
        confirmLoading={mdCreateUser.isPending || mdUpdateUser.isPending}
        okText="Зберегти"
        cancelText="Скасувати"
        width={520}
        destroyOnClose
      >
        <Form form={mdForm} layout="vertical" style={{ marginTop: 16 }}>
          {mdIsCreating && (
            <>
              <Form.Item
                name="userName"
                label="Логін"
                rules={[{ required: true, message: 'Введіть логін' }]}
              >
                <Input maxLength={100} />
              </Form.Item>
              <Form.Item
                name="password"
                label="Пароль"
                rules={[
                  { required: true, message: 'Введіть пароль' },
                  { min: 8, message: 'Мінімум 8 символів' },
                ]}
              >
                <Input.Password />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Введіть email' },
              { type: 'email', message: 'Невалідний email' },
            ]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Прізвище"
            rules={[{ required: true, message: 'Введіть прізвище' }]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            name="firstName"
            label="Ім'я"
            rules={[{ required: true, message: "Введіть ім'я" }]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="middleName" label="По-батькові">
            <Input maxLength={100} />
          </Form.Item>
          {!mdIsCreating && (
            <Form.Item name="phoneNumber" label="Телефон">
              <Input maxLength={20} />
            </Form.Item>
          )}
          <Form.Item
            name="roles"
            label="Ролі"
            rules={[{ required: true, message: 'Оберіть хоча б одну роль' }]}
          >
            <Select
              mode="multiple"
              placeholder="Оберіть ролі"
              options={
                mdRolesList?.map((mdR) => ({ label: mdR, value: mdR })) ?? []
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
